import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import * as L from 'leaflet';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const geoParse = require('wellknown');

export type LeafletMapWrapperStatus = {
  tilesFailed: boolean;
  geometryFailed: boolean;
  debugSummary: string;
};

@Component({
  selector: 'app-leaflet-map-wrapper',
  exportAs: 'leafletMapWrapper',
  templateUrl: './leaflet-map-wrapper.component.html',
  styleUrls: ['./leaflet-map-wrapper.component.scss'],
})
export class LeafletMapWrapperComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() rawGeoJson: any;
  @Input() geoJson: any;

  /**
   * How Point geometries are rendered.
   * - 'circle' (default): circle marker (SVG) with `pathStyle`
   * - 'marker': pin marker icon (matches Task page look)
   */
  @Input() pointRenderMode: 'circle' | 'marker' = 'circle';

  @Input() interactive = false;
  @Input() showZoomControl = false;
  @Input() showAttribution = false;

  // Basemap behavior intended to match the Task page AOI preview
  @Input() defaultBaseLayer: 'Sentinel' | 'Openstreetmap' = 'Sentinel';
  @Input() sentinelOpacity = 0.7;
  @Input() fallbackToOsmOnTileError = true;

  @Input() initialCenter: [number, number] = [25.3176, 82.9739];
  @Input() initialZoom = 3;

  /**
   * Zoom level used when fitting to a single Point (POI).
   * `fitBounds()` on a point-like bounds can zoom excessively; `setView()` is more predictable.
   */
  @Input() pointFitZoom = 11;

  @Input() fitToData = true;
  @Input() fitPadding: [number, number] = [16, 16];

  @Input() pathStyle: L.PathOptions = {
    color: '#1059a9',
    weight: 3,
    opacity: 1,
    fillOpacity: 0.08,
    className: 'aoi-capture-layer',
    interactive: false,
  };

  @Output() statusChange = new EventEmitter<LeafletMapWrapperStatus>();
  @Output() mapReady = new EventEmitter<L.Map>();

  @ViewChild('mapContainer') private readonly mapContainer?: ElementRef<HTMLDivElement>;

  private map: L.Map | null = null;
  private dataLayer: L.Layer | null = null;

  private tilesFailed = false;
  private geometryFailed = false;
  private debugSummary = '';

  ngAfterViewInit(): void {
    this.ensureMap();
    this.renderData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['interactive'] || changes['showZoomControl'] || changes['showAttribution']) {
      // options are only applied at construction time; don’t rebuild yet.
    }
    if (changes['rawGeoJson'] || changes['geoJson']) {
      this.renderData();
    }
  }

  ngOnDestroy(): void {
    try {
      this.map?.remove();
    } catch {
      // ignore
    }
    this.map = null;
    this.dataLayer = null;
  }

  getCaptureElement(): HTMLElement | null {
    return this.mapContainer?.nativeElement ?? null;
  }

  invalidateSize(): void {
    try {
      this.map?.invalidateSize();
    } catch {
      // ignore
    }
  }

  private setStatus(partial: Partial<LeafletMapWrapperStatus>): void {
    if (typeof partial.tilesFailed === 'boolean') this.tilesFailed = partial.tilesFailed;
    if (typeof partial.geometryFailed === 'boolean') this.geometryFailed = partial.geometryFailed;
    if (typeof partial.debugSummary === 'string') this.debugSummary = partial.debugSummary;
    this.statusChange.emit({
      tilesFailed: this.tilesFailed,
      geometryFailed: this.geometryFailed,
      debugSummary: this.debugSummary,
    });
  }

  private ensureMap(): void {
    if (this.map) return;
    const el = this.mapContainer?.nativeElement;
    if (!el) return;

    this.setStatus({ tilesFailed: false, geometryFailed: false, debugSummary: '' });

    this.map = L.map(el, {
      attributionControl: this.showAttribution,
      zoomControl: this.showZoomControl,
      dragging: this.interactive,
      scrollWheelZoom: this.interactive,
      doubleClickZoom: this.interactive,
      boxZoom: this.interactive,
      keyboard: this.interactive,
      touchZoom: this.interactive,
    });

    this.map.setView(this.initialCenter, this.initialZoom);

    // The credit line, spelled exactly as every other map in this app spells it
    // (`leaflet-newcollections`, `leaflet-task`, `previescreen`, …) — the same
    // prefix without Leaflet 1.9's default flag, and the same per-basemap credit.
    // Carried on the LAYERS rather than added to the control by hand, so the
    // credit follows the basemap automatically, including the OSM fallback below.
    if (this.showAttribution) {
      this.map.attributionControl.setPrefix('<a href="https://leafletjs.com/">Leaflet</a>');
    }

    const osm = L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      crossOrigin: true,
      attribution:
        '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> contributers',
    });

    const sentinel = L.tileLayer(
      'https://tiles.maps.eox.at/wmts/1.0.0/s2cloudless-2020_3857/default/g/{z}/{y}/{x}.jpg',
      {
        tileSize: 256,
        opacity: this.sentinelOpacity,
        crossOrigin: true,
        attribution:
          '<a href="https://scihub.copernicus.eu/" target="_blank">Copernicus Sentinel data 2020</a>',
      },
    );

    const onTileError = () => {
      if (!this.tilesFailed) {
        this.setStatus({ tilesFailed: true });
      }
      if (this.fallbackToOsmOnTileError && this.map) {
        try {
          if (this.map.hasLayer(sentinel)) this.map.removeLayer(sentinel);
          if (!this.map.hasLayer(osm)) osm.addTo(this.map);
        } catch {
          // ignore
        }
      }
    };

    osm.on('tileerror', onTileError);
    sentinel.on('tileerror', onTileError);

    if (this.defaultBaseLayer === 'Openstreetmap') {
      osm.addTo(this.map);
    } else {
      sentinel.addTo(this.map);
    }

    this.mapReady.emit(this.map);

    // Dialog containers often size after initial render; invalidate a few times.
    const invalidate = () => this.invalidateSize();
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => invalidate());
      requestAnimationFrame(() => invalidate());
    }
    setTimeout(() => invalidate(), 0);
    setTimeout(() => invalidate(), 250);
  }

  private normalizeGeoJson(raw: any): any | null {
    const maxJsonDepth = 5;

    const tryParseJsonDeep = (value: any): any => {
      let v = value;
      for (let i = 0; i < maxJsonDepth; i++) {
        if (typeof v !== 'string') break;
        const t = v.trim();
        if (!t) break;
        if (!(t.startsWith('{') || t.startsWith('[') || t.startsWith('"'))) break;
        try {
          v = JSON.parse(t);
        } catch {
          break;
        }
      }
      return v;
    };

    const isNum = (n: any) => typeof n === 'number' && Number.isFinite(n);
    const isPair = (p: any) => Array.isArray(p) && p.length === 2 && isNum(p[0]) && isNum(p[1]);
    const samePair = (a: any, b: any) => isPair(a) && isPair(b) && a[0] === b[0] && a[1] === b[1];

    const hasMeaningfulValue = (v: any) => {
      if (v == null) return false;
      if (typeof v === 'string') return !!v.trim();
      return true;
    };

    const coerce = (value: any): any | null => {
      if (value == null) return null;

      if (typeof value === 'object' && !Array.isArray(value)) {
        if (hasMeaningfulValue(value.geoJSONCue)) return coerce(value.geoJSONCue);
        if (hasMeaningfulValue(value.geoJSON)) return coerce(value.geoJSON);
        if (hasMeaningfulValue(value.target)) return coerce(value.target);
        if (hasMeaningfulValue(value.geometry)) return coerce(value.geometry);

        if (Array.isArray(value.multiTarget) && value.multiTarget.length) {
          const geometries = value.multiTarget
            .map((t: any) => coerce(t))
            .map((g: any) => {
              if (!g) return null;
              if (g.type === 'Feature') return g.geometry;
              if (g.type === 'FeatureCollection') return null;
              if (g.type && g.coordinates) return g;
              return null;
            })
            .filter((g: any) => !!g);

          return {
            type: 'FeatureCollection',
            features: geometries.map((g: any) => ({
              type: 'Feature',
              properties: {},
              geometry: g,
            })),
          };
        }
      }

      const v = tryParseJsonDeep(value);
      if (v !== value) return coerce(v);

      if (typeof value === 'string') {
        try {
          const g = geoParse(value);
          if (g?.type && g?.coordinates) {
            return { type: 'Feature', properties: {}, geometry: g };
          }
        } catch {
          // ignore
        }
        return null;
      }

      if (Array.isArray(value)) {
        if (isPair(value)) {
          return { type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: value } };
        }

        if (value.length && value.every((p) => isPair(p))) {
          const first = value[0];
          const last = value[value.length - 1];
          const isClosed = samePair(first, last);
          if (isClosed && value.length >= 4) {
            return { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [value] } };
          }
          return { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: value } };
        }

        if (value.length && value.every((ring) => Array.isArray(ring) && ring.length && ring.every((p: any) => isPair(p)))) {
          const firstRing = value[0];
          const isClosed = samePair(firstRing[0], firstRing[firstRing.length - 1]);
          if (isClosed) {
            return { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: value } };
          }
          return { type: 'Feature', properties: {}, geometry: { type: 'MultiLineString', coordinates: value } };
        }

        if (value.length && value.every((g) => g && typeof g === 'object' && !!g.type)) {
          return {
            type: 'FeatureCollection',
            features: value
              .map((g: any) => {
                if (g.type === 'Feature') return g;
                if (g.type && g.coordinates) return { type: 'Feature', properties: {}, geometry: g };
                return null;
              })
              .filter((f: any) => !!f),
          };
        }

        return null;
      }

      if (value?.type === 'Feature' || value?.type === 'FeatureCollection') return value;
      if (value?.type && value?.coordinates) return { type: 'Feature', properties: {}, geometry: value };

      return null;
    };

    return coerce(raw);
  }

  private normalizeGeometryByCoordinates(geometry: any): any {
    if (!geometry || typeof geometry !== 'object') return geometry;

    const coords = geometry.coordinates;
    const isNum = (n: any) => typeof n === 'number' && Number.isFinite(n);
    const isPair = (p: any) => Array.isArray(p) && p.length === 2 && isNum(p[0]) && isNum(p[1]);
    const samePair = (a: any, b: any) => isPair(a) && isPair(b) && a[0] === b[0] && a[1] === b[1];

    if (geometry.type === 'Point') {
      if (isPair(coords)) return geometry;

      if (Array.isArray(coords) && coords.length && coords.every((p: any) => isPair(p))) {
        const isClosed = coords.length >= 4 && samePair(coords[0], coords[coords.length - 1]);
        return isClosed
          ? { type: 'Polygon', coordinates: [coords] }
          : { type: 'LineString', coordinates: coords };
      }

      if (
        Array.isArray(coords) &&
        coords.length &&
        coords.every((ring: any) => Array.isArray(ring) && ring.length && ring.every((p: any) => isPair(p)))
      ) {
        return { type: 'Polygon', coordinates: coords };
      }

      if (
        Array.isArray(coords) &&
        coords.length &&
        coords.every(
          (poly: any) =>
            Array.isArray(poly) &&
            poly.length &&
            poly.every((ring: any) => Array.isArray(ring) && ring.length && ring.every((p: any) => isPair(p))),
        )
      ) {
        return { type: 'MultiPolygon', coordinates: coords };
      }
    }

    if (geometry.type === 'Polygon' && isPair(coords)) {
      return { type: 'Point', coordinates: coords };
    }

    return geometry;
  }

  private buildLayerFromGeoJson(geoJson: any): L.Layer | null {
    const style = this.pathStyle;
    const toLatLng = (coord: any): [number, number] => [Number(coord?.[1]), Number(coord?.[0])];

    const createPinIcon = (): L.Icon => {
      // Use the same icon assets as the Task map. `className` helps keep it visible in AOI-only exports.
      return L.icon({
        iconUrl: 'assets/map-icons/marker-icon.png',
        shadowUrl: 'assets/map-icons/marker-shadow.png',
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41],
        shadowAnchor: [12, 41],
        className: 'aoi-capture-layer aoi-target-marker',
      });
    };

    const addGeometry = (geometry: any): L.Layer | null => {
      if (!geometry?.type) return null;

      const g = this.normalizeGeometryByCoordinates(geometry);
      if (!g?.type) return null;

      switch (g.type) {
        case 'Point': {
          const [lng, lat] = g.coordinates || [];
          if (typeof lat !== 'number' || typeof lng !== 'number') return null;
          if (this.pointRenderMode === 'marker') {
            return L.marker([lat, lng], { icon: createPinIcon(), interactive: false });
          }
          return L.circleMarker([lat, lng], { ...style, radius: 6, fillOpacity: 0.4 });
        }
        case 'LineString': {
          const coords = Array.isArray(g.coordinates) ? g.coordinates : [];
          return L.polyline(coords.map(toLatLng), { ...style, fillOpacity: 0 });
        }
        case 'MultiLineString': {
          const lines = Array.isArray(g.coordinates) ? g.coordinates : [];
          return L.featureGroup(
            lines.map((line: any) => L.polyline((line || []).map(toLatLng), { ...style, fillOpacity: 0 })),
          );
        }
        case 'Polygon': {
          const rings = Array.isArray(g.coordinates) ? g.coordinates : [];
          const latlngs = rings.map((ring: any) => (ring || []).map(toLatLng));
          return L.polygon(latlngs, style);
        }
        case 'MultiPolygon': {
          const polys = Array.isArray(g.coordinates) ? g.coordinates : [];
          return L.featureGroup(
            polys.map((poly: any) => {
              const rings = Array.isArray(poly) ? poly : [];
              const latlngs = rings.map((ring: any) => (ring || []).map(toLatLng));
              return L.polygon(latlngs, style);
            }),
          );
        }
        default:
          return null;
      }
    };

    if (geoJson?.type === 'FeatureCollection') {
      return L.geoJSON(geoJson, {
        style: () => style,
        pointToLayer: (_feature, latlng) => {
          if (this.pointRenderMode === 'marker') {
            return L.marker(latlng, { icon: createPinIcon(), interactive: false });
          }
          return L.circleMarker(latlng, { ...style, radius: 6, fillOpacity: 0.4 });
        },
      });
    }

    if (geoJson?.type === 'Feature') {
      return addGeometry(geoJson.geometry);
    }

    if (geoJson?.type && geoJson?.coordinates) {
      return addGeometry(geoJson);
    }

    return null;
  }

  private renderData(): void {
    this.ensureMap();
    if (!this.map) return;

    // Clear previous layer
    try {
      if (this.dataLayer && this.map.hasLayer(this.dataLayer)) {
        this.map.removeLayer(this.dataLayer);
      }
    } catch {
      // ignore
    }
    this.dataLayer = null;

    const normalized = this.geoJson ?? this.normalizeGeoJson(this.rawGeoJson);
    if (!normalized) {
      this.setStatus({ geometryFailed: true });
      try {
        const raw = this.geoJson ?? this.rawGeoJson;
        const rawType = Array.isArray(raw) ? 'array' : typeof raw;
        const keys = raw && typeof raw === 'object' && !Array.isArray(raw) ? Object.keys(raw).slice(0, 10).join(',') : '';
        const preview = typeof raw === 'string' ? raw.slice(0, 160) : JSON.stringify(raw)?.slice(0, 160);
        const dbg = `type=${rawType}${keys ? ` keys=[${keys}]` : ''}${preview ? ` preview=${preview}` : ''}`;
        this.setStatus({ debugSummary: dbg });
      } catch {
        // ignore
      }
      return;
    }

    const layer = this.buildLayerFromGeoJson(normalized);
    if (!layer) {
      this.setStatus({ geometryFailed: true });
      try {
        const preview = JSON.stringify(normalized)?.slice(0, 200);
        this.setStatus({ debugSummary: `normalizedType=${normalized?.type || ''}${preview ? ` preview=${preview}` : ''}` });
      } catch {
        // ignore
      }
      return;
    }

    this.setStatus({ geometryFailed: false, debugSummary: '' });

    this.dataLayer = layer.addTo(this.map);

    if (this.fitToData) {
      const anyLayer: any = this.dataLayer as any;
      if (anyLayer && typeof anyLayer.getBounds === 'function') {
        const bounds = anyLayer.getBounds();
        if (bounds && typeof bounds.isValid === 'function' && bounds.isValid()) {
          try {
            const ne = typeof bounds.getNorthEast === 'function' ? bounds.getNorthEast() : null;
            const sw = typeof bounds.getSouthWest === 'function' ? bounds.getSouthWest() : null;
            const isPointLike =
              ne && sw && typeof ne.equals === 'function' ? ne.equals(sw) : !!(ne && sw && ne.lat === sw.lat && ne.lng === sw.lng);

            if (isPointLike) {
              this.map.setView(bounds.getCenter(), this.pointFitZoom);
            } else {
              this.map.fitBounds(bounds, { padding: this.fitPadding });
            }
          } catch {
            this.map.fitBounds(bounds, { padding: this.fitPadding });
          }
        }
      } else if (anyLayer && typeof anyLayer.getLatLng === 'function') {
        // Leaflet Markers don’t have getBounds(); center explicitly for POI targets.
        try {
          const ll = anyLayer.getLatLng();
          if (ll && typeof ll.lat === 'number' && typeof ll.lng === 'number') {
            this.map.setView(ll, this.pointFitZoom);
          }
        } catch {
          // ignore
        }
      }
    }

    // Ensure proper sizing inside dialog/container.
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => this.invalidateSize());
      requestAnimationFrame(() => this.invalidateSize());
    }
    setTimeout(() => this.invalidateSize(), 0);
    setTimeout(() => this.invalidateSize(), 250);
  }
}
